import { createContext, useContext, useEffect, useMemo } from 'react';
import brandingApi from '../services/brandingApi';
import { DEFAULT_BRANDING } from './defaultBranding';

export { DEFAULT_BRANDING };

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  isLoading: true,
  logoUrl: DEFAULT_BRANDING.logoUrl,
  logoDarkUrl: DEFAULT_BRANDING.logoDarkUrl,
});

function resolveHost() {
  try {
    const tenant = new URLSearchParams(window.location.search).get('tenant');
    if (tenant?.trim()) return tenant.trim();
  } catch {
    // ignore
  }
  return window.location.hostname;
}

function normalizeBranding(data) {
  if (!data) return DEFAULT_BRANDING;
  return {
    ...DEFAULT_BRANDING,
    ...data,
    companyName: data.companyName || DEFAULT_BRANDING.companyName,
    primaryColor: data.primaryColor || DEFAULT_BRANDING.primaryColor,
    logoUrl: data.logoUrl || DEFAULT_BRANDING.logoUrl,
    faviconUrl: data.faviconUrl || DEFAULT_BRANDING.faviconUrl,
    nmlsId: data.nmlsId ?? DEFAULT_BRANDING.nmlsId,
  };
}

function applyDocumentBranding(branding) {
  if (typeof document === 'undefined') return;

  document.title = branding.companyName
    ? `${branding.companyName} | Borrower Portal`
    : 'Borrower Portal';

  if (branding.faviconUrl) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.faviconUrl;
  }
}

export function BrandingProvider({ children }) {
  const host = useMemo(() => resolveHost(), []);
  const { data, isLoading, isError } = brandingApi.endpoints.getBranding.useQuery({ host });

  const branding = useMemo(() => {
    if (isError || !data) return DEFAULT_BRANDING;
    return normalizeBranding(data);
  }, [data, isError]);

  useEffect(() => {
    applyDocumentBranding(branding);
  }, [branding]);

  const value = useMemo(
    () => ({
      branding,
      isLoading,
      logoUrl: branding.logoUrl,
      logoDarkUrl: branding.logoDarkUrl,
    }),
    [branding, isLoading],
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export default BrandingProvider;
